import React from "react";
import { Platform, Pressable, StyleSheet } from "react-native";
import { useTranslation } from "react-i18next";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";

type Props = {
  children: React.ReactNode;
};

type State = {
  error: Error | null;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (__DEV__) {
      console.error("[ErrorBoundary]", error, info.componentStack);
    }
  }

  handleReset = () => {
    this.setState({ error: null });
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.location.reload();
    }
  };

  render() {
    if (this.state.error) {
      return <Fallback onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}

function Fallback({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <ThemedView style={styles.container}>
      <ThemedText type="title">{t("ERROR")}</ThemedText>
      <ThemedText style={styles.body}>{t("UNEXPECTED_ERROR")}</ThemedText>
      <Pressable onPress={onReset} style={styles.button}>
        <ThemedText type="link">{t("AREA_REPORT_LOCATION_RETRY")}</ThemedText>
      </Pressable>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  body: {
    marginTop: 12,
    textAlign: "center",
  },
  button: {
    marginTop: 15,
    paddingVertical: 15,
  },
});
